import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Box from './Box';

import { useResponsiveContext } from '../../../contexts/ResponsiveContext';
import Theme from '../../../styles/Theme/Theme';

type TTabsProps = {
    children: React.ReactNode;
    active: number;
    stateId: string;
    bg?: keyof typeof Theme.color;
    setTab: (num: number) => void;
};

type TNavButtonProps = {
    isActive: boolean;
};

const Nav = styled.nav`
    margin-left: 12px;
    margin-bottom: 12px;
    overflow: auto;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const Ul = styled.ul`
    display: flex;
    flex-wrap: nowrap;
    overflow: auto;
    width: max-content;
    /* justify-content: space-between; */
`;

const Li = styled.li`
    list-style-type: none;
    margin-right: 32px;
`;

const NavButton = styled.button<TNavButtonProps>`
    position: relative;
    background-color: transparent;
    border: none;
    color: ${({ isActive, theme }) => (isActive ? 'white' : `${theme.color['neutral3']}`)};;
    cursor: pointer;
`;

const ActiveLine = styled.div<TNavButtonProps>`
    width: 50%;
    height: 5px;
    background-color: ${({ isActive, theme }) => (isActive ? `${theme.color['black-1']}` : null)};
`;

export const Tabs = React.forwardRef<HTMLElement, TTabsProps>((props, ref) => {
    const { children, active = 0, stateId, setTab } = props;

    const navigate = useNavigate();
    const location = useLocation();
    const [viewMode] = useResponsiveContext();
    const [activeTab, setActiveTab] = useState(active);
    const [tabsData, setTabsData] = useState<any>([]);

    useEffect(() => {
        let data: { tab: any; children: any }[] = [];

        React.Children.forEach(children, (element) => {
            if (!React.isValidElement(element)) return;

            const {
                props: { tab, children },
            } = element;
            data.push({ tab, children });
        });

        setTabsData(data);
    }, [children, viewMode]);

    useEffect(() => {
        setActiveTab(active)
    }, [active]);

    const handleNavigation = (idx: number) => {
        setActiveTab(idx);
        setTab(idx);
        navigate(location.pathname, { state: { [stateId]: idx } });
    };

    return (
        <>
            <Nav id="tabs-nav" ref={ref}>
                <Ul>
                    {tabsData.map(({ tab }: any, idx: number) => (
                        <Li key={idx} id={`tabs-li-${idx}`}>
                            <NavButton isActive={idx === activeTab} onClick={() => handleNavigation(idx)}>
                                {tab}
                            </NavButton>
                            <ActiveLine isActive={idx === activeTab} />
                        </Li>
                    ))}
                </Ul>
            </Nav>

            <Box flexGrow={1}>{tabsData[activeTab] && tabsData[activeTab].children}</Box>
        </>
    );
});

type TTabProps = {
    children: React.ReactNode;
    tab: JSX.Element | string;
};

export const Tab: React.FC<TTabProps> = ({ children }): JSX.Element => {
    return <React.Fragment>{children}</React.Fragment>;
};
